import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmtN = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '-';
  if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  if (typeof d === 'string' && d.startsWith('/Date(')) {
    const ms = parseInt(d.replace(/\/Date\((\d+)[^)]*\)\//, '$1'));
    if (!isNaN(ms)) {
      const dt = new Date(ms);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
    }
  }
  const p = new Date(d);
  if (isNaN(p)) return '-';
  return `${String(p.getDate()).padStart(2,'0')}/${String(p.getMonth()+1).padStart(2,'0')}/${p.getFullYear()}`;
};

const PRIMARY = '#1c2266';
const PRIMARY_MID = '#3f51b5';
const PRIMARY_LT = '#e8eaf6';
const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const BUCKETS = [
  { key: 'Age_0_30',   label: '0-30',   bg: '#dcfce7', color: '#166534', hdr: '#166534' },
  { key: 'Age_31_60',  label: '31-60',  bg: '#fef9c3', color: '#854d0e', hdr: '#854d0e' },
  { key: 'Age_61_90',  label: '61-90',  bg: '#fed7aa', color: '#9a3412', hdr: '#9a3412' },
  { key: 'Age_90_Plus',label: '90+',    bg: '#fee2e2', color: '#991b1b', hdr: '#991b1b' },
];

const ClientAgeingFIFO = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [clients, setClients]   = useState([]);
  const [masterId, setMasterId] = useState('');
  const [search, setSearch]     = useState('');

  useEffect(() => {
    if (companyId && token) {
      fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token)
        .then(res => setClients(res?.response_data || res || []))
        .catch(() => setClients([]));
    }
  }, [companyId, token]);

  const fetchReport = async () => {
    //if (!masterId) { setError('Please select a client'); return; }
    setLoading(true);
    try {
      const id = masterId || 0;
      const res = await fetchData('SaleReportApi', `/ClientAgeingFIFO/${companyId}/${branchId}/${id}`, token);
      const rows = res?.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch client ageing FIFO report');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.BillNo || '').toLowerCase().includes(q) ||
      (r.CustomerName || '').toLowerCase().includes(q) ||
      (r.ContactNumber || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const totals = useMemo(() => filtered.reduce((acc, r) => ({
    BillAmount:   acc.BillAmount   + (parseFloat(r.BillAmount)   || 0),
    BalanceAmount:acc.BalanceAmount+ (parseFloat(r.BalanceAmount)|| 0),
    Age_0_30:     acc.Age_0_30     + (parseFloat(r.Age_0_30)     || 0),
    Age_31_60:    acc.Age_31_60    + (parseFloat(r.Age_31_60)    || 0),
    Age_61_90:    acc.Age_61_90    + (parseFloat(r.Age_61_90)    || 0),
    Age_90_Plus:  acc.Age_90_Plus  + (parseFloat(r.Age_90_Plus)  || 0),
  }), { BillAmount: 0, BalanceAmount: 0, Age_0_30: 0, Age_31_60: 0, Age_61_90: 0, Age_90_Plus: 0 }), [filtered]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const name = r.CustomerName || 'Unknown';
      if (!map[name]) map[name] = [];
      map[name].push(r);
    });
    return Object.entries(map).map(([customer, rows]) => ({
      customer,
      rows,
      totals: rows.reduce((acc, r) => ({
        BillAmount:   acc.BillAmount   + (parseFloat(r.BillAmount)   || 0),
        BalanceAmount:acc.BalanceAmount+ (parseFloat(r.BalanceAmount)|| 0),
        Age_0_30:     acc.Age_0_30     + (parseFloat(r.Age_0_30)     || 0),
        Age_31_60:    acc.Age_31_60    + (parseFloat(r.Age_31_60)    || 0),
        Age_61_90:    acc.Age_61_90    + (parseFloat(r.Age_61_90)    || 0),
        Age_90_Plus:  acc.Age_90_Plus  + (parseFloat(r.Age_90_Plus)  || 0),
      }), { BillAmount: 0, BalanceAmount: 0, Age_0_30: 0, Age_31_60: 0, Age_61_90: 0, Age_90_Plus: 0 }),
    }));
  }, [filtered]);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'ClientAgeingFIFO', 0);
    if (!filtered.length) return;
    const clientName = clients.find(c => String(c.MasterId || c.Id) === String(masterId))?.Name || '';
    const headers = ['#', 'Bill No', 'Bill Date', 'Customer', 'Contact',
      'Bill Amount', 'Balance Amount', 'Age Days', '0-30', '31-60', '61-90', '90+'];
    const rows = filtered.map((r, i) => [
      i + 1, r.BillNo, fmtDate(r.BillDate),
      r.CustomerName, r.ContactNumber,
      r.BillAmount, r.BalanceAmount, r.AgeDays,
      r.Age_0_30, r.Age_31_60, r.Age_61_90, r.Age_90_Plus,
    ]);

      await exportStyledExcel({
          reportTitle: 'CLIENT AGEING ANALYSIS - FIFO BASED',
          sheetName: 'Ageing FIFO',
          fileName: `ClientAgeingFIFO_${clientName}.xlsx`,
          companyName,
          username,
          headers: headers,
          rows,
          totalsRow: ['', '', '', '', 'Totals',
        fmtN(totals.BillAmount), fmtN(totals.BalanceAmount), '',
        fmtN(totals.Age_0_30), fmtN(totals.Age_31_60), fmtN(totals.Age_61_90), fmtN(totals.Age_90_Plus)],
          columnWidths: [4,14,12,24,14,14,14,10,12,12,12,12],
      });
  };

  const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #9fa8da', color: '#fff', background: PRIMARY_MID };
  const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading FIFO ageing report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d3494 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Client Ageing Analysis
            <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>FIFO Based</span>
          </span>
          <div style={{ flex: 1, minWidth: 4 }} />
          <input
            placeholder="Search bill / customer..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 160, outline: 'none' }}
          />
          <SearchableDropdown
            value={masterId}
            onChange={v => setMasterId(v)}
            options={clients.map(c => ({ value: String(c.MasterId || c.Id), label: c.Name }))}
            placeholder="— Select Client —"
            minWidth={180}
            primaryColor={PRIMARY}
          />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Bucket summary bar */}
        {fetched && filtered.length > 0 && (
          <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
            {BUCKETS.map(b => (
              <div key={b.key} style={{ background: b.bg, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110 }}>
                <span style={{ fontSize: 10, color: b.color, fontWeight: 600 }}>{b.label} Days</span>
                <span style={{ fontSize: 12, color: b.color, fontWeight: 700 }}>{fmtN(totals[b.key])}</span>
              </div>
            ))}
            <div style={{ background: '#e8eaf6', borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110 }}>
              <span style={{ fontSize: 10, color: PRIMARY, fontWeight: 600 }}>Balance Total</span>
              <span style={{ fontSize: 12, color: PRIMARY, fontWeight: 700 }}>{fmtN(totals.BalanceAmount)}</span>
            </div>
          </div>
        )}

        {/* Meta */}
        {fetched && (
          <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
            {filtered.length} bills{search ? ` (filtered from ${data.length})` : ''}
            &nbsp;|&nbsp; Bill Amt: <strong style={{ color: PRIMARY }}>{fmtN(totals.BillAmount)}</strong>
            &nbsp;|&nbsp; Balance: <strong style={{ color: '#b91c1c' }}>{fmtN(totals.BalanceAmount)}</strong>
          </div>
        )}

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 400, width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                <th style={{ ...thBase, textAlign: 'left' }}>Bill No</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Bill Date</th>
                <th style={{ ...thBase, textAlign: 'left', minWidth: 180 }}>Customer</th>
                <th style={{ ...thBase, textAlign: 'left' }}>Contact</th>
                <th style={{ ...thBase, textAlign: 'right' }}>Bill Amount</th>
                <th style={{ ...thBase, textAlign: 'right', background: '#0f4c81' }}>Balance</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Age Days</th>
                {BUCKETS.map(b => (
                  <th key={b.key} style={{ ...thBase, textAlign: 'right', background: b.hdr }}>{b.label} Days</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select a client and click Refresh'}
                  </td>
                </tr>
              ) : (
                grouped.map((group, gi) => {
                  let rowIdx = 0;
                  return (
                    <React.Fragment key={group.customer}>
                      {/* Customer group header */}
                      <tr>
                        <td colSpan={12} style={{ ...tdBase, background: '#e8eaf6', fontWeight: 700, fontSize: 12, color: PRIMARY, padding: '6px 10px', borderTop: gi > 0 ? '2px solid #9fa8da' : undefined }}>
                          {group.customer} ({group.rows.length} bill{group.rows.length > 1 ? 's' : ''})
                        </td>
                      </tr>
                      {group.rows.map((r, i) => {
                        rowIdx++;
                        const rowBg = rowIdx % 2 === 0 ? '#fff' : '#f8f9ff';
                        const ageDays = r.AgeDays || 0;
                        const ageColor = ageDays > 90 ? '#991b1b' : ageDays > 60 ? '#9a3412' : ageDays > 30 ? '#854d0e' : '#166534';
                        return (
                          <tr key={`${r.SaleId}-${i}`} style={{ background: rowBg }}>
                            <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                            <td style={{ ...tdBase, fontWeight: 500, color: PRIMARY }}>{r.BillNo || '-'}</td>
                            <td style={{ ...tdBase, textAlign: 'center' }}>{fmtDate(r.BillDate)}</td>
                            <td style={{ ...tdBase, fontWeight: 500 }}>{r.CustomerName}</td>
                            <td style={{ ...tdBase, color: '#0277bd' }}>{r.ContactNumber || '-'}</td>
                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600 }}>{fmtN(r.BillAmount)}</td>
                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fff5f5' }}>{fmtN(r.BalanceAmount)}</td>
                            <td style={{ ...tdBase, textAlign: 'center', fontWeight: 700, color: ageColor }}>{ageDays}</td>
                            {BUCKETS.map(b => {
                              const val = parseFloat(r[b.key]) || 0;
                              return (
                                <td key={b.key} style={{ ...tdBase, textAlign: 'right', background: val ? b.bg : 'transparent', color: val ? b.color : '#cbd5e1', fontWeight: val ? 600 : 400 }}>
                                  {val ? fmtN(val) : '-'}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                      {/* Group subtotal */}
                      <tr style={{ background: '#f1f5f9' }}>
                        <td colSpan={5} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right', fontSize: 10 }}>Subtotal</td>
                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(group.totals.BillAmount)}</td>
                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c' }}>{fmtN(group.totals.BalanceAmount)}</td>
                        <td style={{ ...tdBase }} />
                        {BUCKETS.map(b => (
                          <td key={b.key} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: b.color }}>{fmtN(group.totals[b.key])}</td>
                        ))}
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: '#e8eaf6', fontWeight: 700 }}>
                  <td colSpan={5} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(totals.BillAmount)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fecaca' }}>{fmtN(totals.BalanceAmount)}</td>
                  <td style={{ ...tdBase }} />
                  {BUCKETS.map(b => (
                    <td key={b.key} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: b.color, background: b.bg }}>{fmtN(totals[b.key])}</td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClientAgeingFIFO;




